import { useMemo, useState } from 'react';
import { Check, Filter } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import type {
  ConversationStatusFilter,
  DateFilter,
  HistoryFilters,
} from '../../hooks/useHistoryFilters';

interface CaseOption {
  id: string;
  title: string;
}

interface Props {
  filters: HistoryFilters;
  onDateChange: (next: DateFilter) => void;
  onCaseChange: (next: string | null) => void;
  onStatusChange: (next: ConversationStatusFilter) => void;
  onReset: () => void;
  /** Dossiers offered to the filter — derived from the loaded conversations. */
  caseOptions: CaseOption[];
  /** Archived conversations count, shown next to the « Archivées » toggle. */
  archivedCount: number;
  isFiltered: boolean;
  className?: string;
}

const DATE_ITEMS: { value: DateFilter; label: string }[] = [
  { value: 'all', label: 'Toutes les dates' },
  { value: 'today', label: "Aujourd'hui" },
  { value: '7d', label: '7 derniers jours' },
  { value: '30d', label: '30 derniers jours' },
];

/**
 * Compact filter button that opens a dropdown menu, in this order: Dossier,
 * Archivées, Date. Active conversations are the implicit default, so the status
 * dimension is a single « Archivées » toggle rather than an Actives/Archivées
 * pair. A small dot on the trigger marks any active non-default filter so the
 * lawyer notices the list is restricted.
 *
 * The Dossier section never lists every cabinet case — only the dossiers that
 * appear in the loaded conversations (passed as `caseOptions`). To stay compact
 * regardless of count, it offers a search input + a scrollable capped list.
 */
export function HistoryFilterButton({
  filters,
  onDateChange,
  onCaseChange,
  onStatusChange,
  onReset,
  caseOptions,
  archivedCount,
  isFiltered,
  className = '',
}: Props) {
  const [caseQuery, setCaseQuery] = useState('');

  const filteredCases = useMemo(() => {
    const q = caseQuery.trim().toLowerCase();
    if (!q) return caseOptions;
    return caseOptions.filter((c) => c.title.toLowerCase().includes(q));
  }, [caseOptions, caseQuery]);

  const selectedCaseTitle = caseOptions.find((c) => c.id === filters.caseId)?.title ?? null;

  return (
    <DropdownMenu onOpenChange={(open) => { if (!open) setCaseQuery(''); }}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Filtrer les conversations"
          title="Filtrer les conversations"
          className={`relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-grey-600 transition-colors hover:bg-[#F0F0EE] hover:text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className}`}
        >
          <Filter className="w-4 h-4" />
          {isFiltered && (
            <span
              aria-hidden
              className="pointer-events-none absolute right-0.5 top-0.5 h-1 w-1 rounded-full bg-charcoal"
            />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60" aria-label="Filtres de conversation">
        {(caseOptions.length > 0 || filters.caseId !== null) && (
          <>
            <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wide text-[#888888]">
              Dossier
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => onCaseChange(null)}
              className="text-xs flex items-center justify-between"
            >
              <span className="text-grey-500">Tous les dossiers</span>
              {filters.caseId === null && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
            </DropdownMenuItem>
            {/* The full dossier list (search + scroll) lives in a SIDE sub-panel so
                the main filter menu stays short and keeps opening in place. */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="text-xs">
                <span className="flex-1 truncate">
                  {selectedCaseTitle ?? 'Choisir un dossier'}
                </span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-60 p-0">
                <div className="p-2">
                  <Input
                    value={caseQuery}
                    onChange={(e) => setCaseQuery(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder="Rechercher un dossier..."
                    className="h-7 text-xs"
                    autoComplete="off"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto pb-1 scrollbar-thin scrollbar-thumb-[#969691] scrollbar-track-transparent">
                  {filteredCases.length === 0 ? (
                    <div className="px-2 py-2 text-xs text-grey-500">Aucun dossier</div>
                  ) : (
                    filteredCases.map((c) => (
                      <DropdownMenuItem
                        key={c.id}
                        onClick={() => onCaseChange(c.id)}
                        className="text-xs flex items-center justify-between gap-2"
                      >
                        <span className="truncate">{c.title}</span>
                        {filters.caseId === c.id && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                      </DropdownMenuItem>
                    ))
                  )}
                </div>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem
          onClick={() => onStatusChange(filters.status === 'archived' ? 'active' : 'archived')}
          className="text-xs flex items-center justify-between"
        >
          <span>Archivées ({archivedCount})</span>
          {filters.status === 'archived' && <Check className="w-3.5 h-3.5" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wide text-[#888888]">
          Date
        </DropdownMenuLabel>
        {DATE_ITEMS.map((item) => (
          <DropdownMenuItem
            key={item.value}
            onClick={() => onDateChange(item.value)}
            className="text-xs flex items-center justify-between"
          >
            <span>{item.label}</span>
            {filters.date === item.value && <Check className="w-3.5 h-3.5" />}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onReset}
          disabled={!isFiltered}
          className="text-xs font-medium text-charcoal"
        >
          Réinitialiser les filtres
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
